import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { SmartRouter } from '../services/Router';
import styled from 'styled-components';
import { mainStore } from '../store/MainStore';
import { contentStore } from '../store/Store';
import { GlobalLanguageToggler } from './GlobalBilanguageToggler';

const ContentHead = ({
    createOnly=false,
    contentName='',
    contentId='',
    backDestination='',
    isSaveLoading=false,
    isPublisheLoading=false,
    onSave=()=>{},
    onPublish=()=>{},
    obj,
}) => {
    const router = new SmartRouter()

    const TitleText = () => {
        const text = contentId ? 'Edit an Entry' : 'Create an Entry'
        const textStyle = {
            fontSize: 32,
            fontWeight: 700,
        }
        return <Typography style={textStyle}>{text}</Typography>
    }

    const EntryInformation = () => {
        const informationStyle = {
            fontSize: 16,
            fontWeight: 400,
            lineHeight: '18px',
        }

        const ContentText = () => 
            <Typography color='primary.disabled' style={informationStyle}> 
                {`Content name: ${contentName || ''}`} 
            </Typography>

        const IdText = () => 
            <Typography color='primary.disabled' style={informationStyle}> 
                {`ID: ${contentId || ''}`}
            </Typography>

        return (
            <div>
                <ContentText />
                <IdText />
            </div>
        );
    }

    const BackButton = () => {
        const onClick = () => {
            router.to(backDestination)
        }
        return <Button 
            startIcon={<ArrowBackIcon />} 
            size='small'
            onClick={onClick}
        >
            back
        </Button>
    }

    const SaveButtonNotObserved = () => {
        const SaveIcon = [true, null].includes(mainStore.isSaved) && contentId ? 
            <DoneAllIcon /> : <DoneIcon /> 

        return <Box sx={{ m: 1, margin: 0, position: 'relative', justifySelf: 'end'}}>
                <Button 
                style={{
                    height: 'max-content',
                    width: 'max-content',
                }}
                variant='contained'
                startIcon={SaveIcon}
                onClick={onSave}
                disabled={mainStore.isSaved === null || isSaveLoading || createOnly && contentStore.item.id}
                disableRipple={mainStore.isSaved}
            >
                Save
            </Button>
            {isSaveLoading && (
                <CircularProgress
                    size={24}
                    sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-18px',
                    }}
                />
            )}
        </Box>
    }

    const SaveButton = observer(SaveButtonNotObserved)

    const PublishButtonNotObserved = () => {
        const isPublished = !!obj.isPublished

        const isDisabled =
            obj?.isPublished === null ||
            isPublisheLoading ||
            !mainStore?.isSaved && mainStore?.isSaved!==null ||
            createOnly

        const segmentStyle = (active, color) => ({
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            padding: '0 16px',
            fontSize: 14,
            fontWeight: 700,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            color: isDisabled ? 'text.disabled' : (active ? `${color}.main` : 'text.secondary'),
            transition: 'color 0.2s ease',
            '&::after': {
                content: '""',
                position: 'absolute',
                left: 16,
                right: 16,
                bottom: 6,
                height: '2px',
                borderRadius: '1px',
                backgroundColor: isDisabled ? 'transparent' : `${color}.main`,
                transform: active && !isDisabled ? 'scaleX(1)' : 'scaleX(0)',
                transition: 'transform 0.25s ease',
            },
        })

        return <Box sx={{ m: 1, margin: 0, position: 'relative', justifySelf: 'start', flexShrink: 0}}>
            <Box
                role="switch"
                aria-checked={isPublished}
                aria-disabled={isDisabled}
                onClick={isDisabled ? undefined : onPublish}
                sx={{
                    display: 'inline-flex',
                    flexShrink: 0,
                    height: '36.5px',
                    boxSizing: 'border-box',
                    border: '1px solid',
                    borderColor: isDisabled ? 'action.disabled' : (isPublished ? 'primary.main' : 'warning.main'),
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: isDisabled ? 'default' : 'pointer',
                }}
            >
                <Box sx={segmentStyle(isPublished, 'primary')}>published</Box>
                <Box sx={{ width: '1px', bgcolor: 'divider' }} />
                <Box sx={segmentStyle(!isPublished, 'warning')}>in draft</Box>
            </Box>
            {isPublisheLoading && (
                <CircularProgress
                    size={24}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: obj.isPublished ? '-14px' : '-18px',
                    }}
                />
            )}
        </Box>
    }

    const PublishButton = observer(PublishButtonNotObserved)

    const HeadContainer = styled.div`
        display: grid;
        grid-area: head;
        grid-template-columns: subgrid;
        grid-column: 1 / 3;
        grid-template-rows: auto auto;
        row-gap: 12px;
        margin: 0 0 8px 0;
    `

    const ButtonsContainer = styled.div`
        display: flex;
        align-items: end;
        justify-content: flex-end;
        gap: 12px;
    `

    return (
        <HeadContainer>
            <div style={{ gridColumn: 1, gridRow: 1 }}>
                <BackButton />
                <TitleText />
                <EntryInformation />
            </div>
            <div style={{ gridColumn: '1 / 3', gridRow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <ButtonsContainer>
                    <PublishButton/>
                    <SaveButton/>
                </ButtonsContainer>
            </div>
            {contentName !== 'Users' && (
                <div style={{ gridColumn: 1, gridRow: 2, justifySelf: 'end', paddingRight: 16 }}>
                    <GlobalLanguageToggler />
                </div>
            )}
        </HeadContainer>
    );
}

const observed = observer(ContentHead)
export {observed as ContentHead};